import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  listarAlertas,
  contarAlertas,
  marcarLido,
  marcarResolvido,
  marcarArquivado,
  marcarTodosLidos,
  listarRegras,
  atualizarRegra,
} from '../../../services/alertaService';

// ============================================================
// Slice de Alertas Proativos (T2 / Camada 2).
// Estado:
//   - lista          : array de alertas correntemente carregados (com filtros)
//   - contadores     : badge counts ({ novo, lido, criticos_abertos, total_abertos })
//   - regras         : array de regras configuraveis
//   - filtros        : filtros aplicados a `lista` (status, severidade, codigo)
//
// Polling do sino: a HeaderBell faz dispatch(fetchContadores()) a cada 60s.
// ============================================================

const initialState = {
  lista: [],
  contadores: { novo: 0, lido: 0, criticos_abertos: 0, total_abertos: 0 },
  regras: [],
  filtros: { status: '', severidade: '', codigo: '', limit: 50, offset: 0 },
  loadingLista: false,
  loadingContadores: false,
  loadingRegras: false,
  error: null,
};

export const fetchAlertas = createAsyncThunk(
  'alertas/fetchLista',
  async (params, { getState }) => {
    const merged = { ...getState().alertasReducer.filtros, ...(params || {}) };
    return listarAlertas(merged);
  },
);

export const fetchContadores = createAsyncThunk(
  'alertas/fetchContadores',
  async () => contarAlertas(),
);

export const fetchRegras = createAsyncThunk(
  'alertas/fetchRegras',
  async () => listarRegras(),
);

export const marcarLidoThunk = createAsyncThunk(
  'alertas/marcarLido',
  async (id, { dispatch }) => {
    const resp = await marcarLido(id);
    dispatch(fetchContadores());
    return { id, resp };
  },
);

export const marcarResolvidoThunk = createAsyncThunk(
  'alertas/marcarResolvido',
  async (id, { dispatch }) => {
    const resp = await marcarResolvido(id);
    dispatch(fetchContadores());
    return { id, resp };
  },
);

export const marcarArquivadoThunk = createAsyncThunk(
  'alertas/marcarArquivado',
  async (id, { dispatch }) => {
    const resp = await marcarArquivado(id);
    dispatch(fetchContadores());
    return { id, resp };
  },
);

export const marcarTodosLidosThunk = createAsyncThunk(
  'alertas/marcarTodosLidos',
  async (_, { dispatch }) => {
    const resp = await marcarTodosLidos();
    dispatch(fetchContadores());
    dispatch(fetchAlertas());
    return resp;
  },
);

export const atualizarRegraThunk = createAsyncThunk(
  'alertas/atualizarRegra',
  async ({ id, payload }, { dispatch }) => {
    const resp = await atualizarRegra(id, payload);
    dispatch(fetchRegras());
    return resp;
  },
);

const AlertasSlice = createSlice({
  name: 'alertas',
  initialState,
  reducers: {
    setFiltros: (state, action) => {
      state.filtros = { ...state.filtros, ...action.payload };
    },
    limparErro: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAlertas.pending, (state) => {
        state.loadingLista = true;
        state.error = null;
      })
      .addCase(fetchAlertas.fulfilled, (state, action) => {
        state.loadingLista = false;
        state.lista = action.payload || [];
      })
      .addCase(fetchAlertas.rejected, (state, action) => {
        state.loadingLista = false;
        state.error = action.error?.message || 'Falha ao carregar alertas';
      })

      .addCase(fetchContadores.pending, (state) => {
        state.loadingContadores = true;
      })
      .addCase(fetchContadores.fulfilled, (state, action) => {
        state.loadingContadores = false;
        state.contadores = action.payload || initialState.contadores;
      })
      .addCase(fetchContadores.rejected, (state) => {
        state.loadingContadores = false;
      })

      .addCase(fetchRegras.pending, (state) => {
        state.loadingRegras = true;
      })
      .addCase(fetchRegras.fulfilled, (state, action) => {
        state.loadingRegras = false;
        state.regras = action.payload || [];
      })
      .addCase(fetchRegras.rejected, (state, action) => {
        state.loadingRegras = false;
        state.error = action.error?.message || 'Falha ao carregar regras';
      })

      .addCase(marcarLidoThunk.fulfilled, (state, action) => {
        const item = state.lista.find((a) => a.id === action.payload.id);
        if (item) item.status = 'lido';
      })
      .addCase(marcarResolvidoThunk.fulfilled, (state, action) => {
        state.lista = state.lista.filter((a) => a.id !== action.payload.id);
      })
      .addCase(marcarArquivadoThunk.fulfilled, (state, action) => {
        state.lista = state.lista.filter((a) => a.id !== action.payload.id);
      });
  },
});

export const { setFiltros, limparErro } = AlertasSlice.actions;
export default AlertasSlice.reducer;
